import { TrainingZoneField } from './TrainingZoneEditor';
import { useTranslation } from 'react-i18next';
import { displayInputDigits } from '../../utils/localizedInput';
import React, { useMemo } from 'react';
import {
  DISTANCE_5K_M,
  DISTANCE_10K_M,
  formatPaceSeconds,
  isDistanceTimeMethod,
  paceSecondsPerKmFromSpeedMps,
  parseRaceTimeString,
  thresholdSpeedFromRace,
  thresholdSpeedFromRaces,
} from '../../utils/paceZoneHelpers';

export interface PaceZoneCalculatorProps {
  calculationTypeId?: number;
  race5kTime: string;
  race10kTime: string;
  thresholdPaceTime: string;
  savedThresholdPace?: string | null;
  onRace5kChange: (v: string) => void;
  onRace10kChange: (v: string) => void;
  onThresholdPaceChange: (v: string) => void;
}

/** Race-time or direct-threshold inputs for running pace zones. */
export function PaceZoneCalculator({
  calculationTypeId,
  race5kTime,
  race10kTime,
  thresholdPaceTime,
  savedThresholdPace,
  onRace5kChange,
  onRace10kChange,
  onThresholdPaceChange,
}: PaceZoneCalculatorProps) {
  const { t, i18n } = useTranslation();
  const useRaceTimes = isDistanceTimeMethod(calculationTypeId);

  const previewPace = useMemo(() => {
    if (useRaceTimes) {
      const races: { distance: number; timeSeconds: number }[] = [];
      const t5 = parseRaceTimeString(race5kTime);
      const t10 = parseRaceTimeString(race10kTime);
      if (t5) races.push({ distance: DISTANCE_5K_M, timeSeconds: t5 });
      if (t10) races.push({ distance: DISTANCE_10K_M, timeSeconds: t10 });
      if (!races.length) return null;
      const speed = races.length === 1
        ? thresholdSpeedFromRace(races[0].distance, races[0].timeSeconds)
        : thresholdSpeedFromRaces(races);
      return formatPaceSeconds(paceSecondsPerKmFromSpeedMps(speed));
    }
    const paceSec = parseRaceTimeString(thresholdPaceTime);
    if (!paceSec) return null;
    return formatPaceSeconds(paceSec);
  }, [useRaceTimes, race5kTime, race10kTime, thresholdPaceTime]);

  return <div className="training-zone-editor__pace-inputs">
    <p className="power-zone-editor__hint">{t(useRaceTimes ? 'athleteSettings.trainingEditor.raceHint' : 'athleteSettings.trainingEditor.paceHint')}</p>
    {useRaceTimes ? <>
      <TrainingZoneField kind="pace" label={t('athleteSettings.trainingEditor.race5')} value={race5kTime} onChange={onRace5kChange} placeholder="25:00" />
      <TrainingZoneField kind="pace" label={t('athleteSettings.trainingEditor.race10')} value={race10kTime} onChange={onRace10kChange} placeholder="52:00" />
    </> : <TrainingZoneField kind="pace" label={t('athleteSettings.trainingEditor.threshold')} value={thresholdPaceTime} onChange={onThresholdPaceChange} placeholder="5:15" />}
    {previewPace && parseRaceTimeString(previewPace) !== parseRaceTimeString(savedThresholdPace ?? '') && <div className="training-zone-editor__summary"><span>{t('athleteSettings.trainingEditor.estimated')}</span><strong dir="ltr">{displayInputDigits(previewPace, i18n.language)}</strong><span>{t('athleteSettings.trainingEditor.perKm')}</span></div>}
  </div>;
}
